"use client";

import { toast as sonner } from "sonner";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";

/* ─────────────────────────────────────────────────────────────
   SNA toast layer (partner portal)
   · drop-in replacement for `import { toast } from "sonner"`
   · every message (server or client) is translated to the
     active locale through the dictionary below
   · custom luxury card (navy / gold), RTL-aware
   ───────────────────────────────────────────────────────────── */

type Kind = "success" | "error" | "warning" | "info";
type Pair = { ar: string; en: string };

/* key = message as emitted by the server / client (any language) */
const DICT: Record<string, Pair> = {
  /* auth */
  "Login successful": { ar: "تم تسجيل الدخول بنجاح", en: "Signed in successfully" },
  "Login successful!": { ar: "تم تسجيل الدخول بنجاح", en: "Signed in successfully" },
  "Logout successful": { ar: "تم تسجيل الخروج", en: "Signed out" },
  "Registration successful!": { ar: "تم إنشاء الحساب، تحقق من بريدك الإلكتروني", en: "Account created — check your email" },
  "Invalid credentials": { ar: "البريد الإلكتروني أو كلمة المرور غير صحيحة", en: "Incorrect email or password" },
  "Email not verified please verify your email": { ar: "يرجى توثيق بريدك الإلكتروني أولاً", en: "Please verify your email first" },
  "Email already in use": { ar: "البريد الإلكتروني مستخدم بالفعل", en: "This email is already in use" },
  "Email already in use by a developer": { ar: "البريد الإلكتروني مستخدم بالفعل", en: "This email is already in use" },
  "Email or phone number already exists": { ar: "البريد الإلكتروني أو رقم الهاتف مستخدم بالفعل", en: "Email or phone number already exists" },
  "Phone number already in use": { ar: "رقم الهاتف مستخدم بالفعل", en: "This phone number is already in use" },
  "Password reset link sent!": { ar: "تم إرسال رابط إعادة تعيين كلمة المرور", en: "Password reset link sent" },
  "Password updated successfully": { ar: "تم تحديث كلمة المرور", en: "Password updated" },
  "User password updated successfully!": { ar: "تم تحديث كلمة المرور", en: "Password updated" },
  "Current password is incorrect": { ar: "كلمة المرور الحالية غير صحيحة", en: "Current password is incorrect" },
  "الحساب غير نشط، يرجى التواصل مع الدعم لتفعيل حسابك": { ar: "الحساب غير نشط، يرجى التواصل مع الدعم لتفعيل حسابك", en: "Account inactive — please contact support to activate it" },
  "الحساب غير موجود": { ar: "الحساب غير موجود", en: "Account not found" },
  "Unauthorized": { ar: "يرجى تسجيل الدخول أولاً", en: "Please sign in first" },
  "Access denied": { ar: "غير مصرح لك بهذا الإجراء", en: "You're not allowed to do this" },
  "Forbidden": { ar: "غير مصرح لك بهذا الإجراء", en: "You're not allowed to do this" },
  "Permission denied": { ar: "غير مصرح لك بهذا الإجراء", en: "You're not allowed to do this" },
  "you don't have permission to perform this action": { ar: "غير مصرح لك بهذا الإجراء", en: "You're not allowed to do this" },
  "User not found": { ar: "المستخدم غير موجود", en: "User not found" },
  "Developer not found": { ar: "الحساب غير موجود", en: "Account not found" },

  /* profile */
  "Profile updated successfully": { ar: "تم تحديث بيانات الحساب", en: "Profile updated" },
  "Developer profile updated successfully": { ar: "تم تحديث بيانات الحساب", en: "Profile updated" },
  "Agent profile updated successfully": { ar: "تم تحديث بيانات الحساب", en: "Profile updated" },
  "تم تحديث بيانات الحساب بنجاح": { ar: "تم تحديث بيانات الحساب", en: "Profile updated" },
  "Failed to update profile": { ar: "تعذّر تحديث بيانات الحساب", en: "Couldn't update your profile" },
  "Profile image updated successfully": { ar: "تم تحديث الصورة الشخصية", en: "Profile photo updated" },
  "Media updated successfully": { ar: "تم تحديث الصورة الشخصية", en: "Profile photo updated" },
  "جاري رفع الصورة...": { ar: "جارٍ رفع الصورة…", en: "Uploading photo…" },
  "Verification request submitted successfully!": { ar: "تم إرسال طلب التوثيق، سنراجعه قريباً", en: "Verification request submitted — we'll review it shortly" },

  /* access fee */
  "يجب دفع رسوم الوصول للمنصة قبل استخدام هذه الخدمة": { ar: "يجب دفع رسوم تفعيل المنصة قبل استخدام هذه الخدمة", en: "Pay the platform activation fee to use this service" },
  "تم دفع الرسوم بالفعل": { ar: "تم دفع الرسوم بالفعل", en: "The fee has already been paid" },
  "لا توجد رسوم مطلوبة على هذا الحساب": { ar: "لا توجد رسوم مطلوبة على هذا الحساب", en: "No fee is required for this account" },
  "تم إنشاء عملية الدفع، يرجى إتمام الدفع.": { ar: "جارٍ تحويلك إلى بوابة الدفع الآمنة", en: "Redirecting you to the secure payment gateway" },
  "Failed to initiate payment": { ar: "تعذّر بدء عملية الدفع", en: "Couldn't start the payment" },
  "Failed to start payment.": { ar: "تعذّر بدء عملية الدفع", en: "Couldn't start the payment" },
  "Failed to create payment": { ar: "تعذّر بدء عملية الدفع", en: "Couldn't start the payment" },
  "Payment successful!": { ar: "تم الدفع بنجاح", en: "Payment successful" },
  "Payment successful.": { ar: "تم الدفع بنجاح", en: "Payment successful" },
  "Payment failed.": { ar: "فشلت عملية الدفع", en: "Payment failed" },

  /* properties */
  "Property added successfully": { ar: "تمت إضافة العقار بنجاح", en: "Property added" },
  "Property added successfully.": { ar: "تمت إضافة العقار بنجاح", en: "Property added" },
  "Property created (free)": { ar: "تمت إضافة العقار (ضمن العقارات المجانية)", en: "Property added (free listing)" },
  "Property created (paid)": { ar: "تمت إضافة العقار", en: "Property added" },
  "Property updated successfully": { ar: "تم تحديث العقار", en: "Property updated" },
  "Property deleted successfully": { ar: "تم حذف العقار", en: "Property deleted" },
  "Property deleted successfully.": { ar: "تم حذف العقار", en: "Property deleted" },
  "Property media updated successfully.": { ar: "تم تحديث صور العقار", en: "Property photos updated" },
  "Property status updated successfully": { ar: "تم تحديث حالة العقار", en: "Property status updated" },
  "Property not found": { ar: "العقار غير موجود", en: "Property not found" },
  "property not found": { ar: "العقار غير موجود", en: "Property not found" },
  "No properties found": { ar: "لا توجد عقارات", en: "No properties found" },
  "Failed to add property.": { ar: "تعذّرت إضافة العقار", en: "Couldn't add the property" },
  "Failed to add new property": { ar: "تعذّرت إضافة العقار", en: "Couldn't add the property" },
  "Failed to create property": { ar: "تعذّرت إضافة العقار", en: "Couldn't add the property" },
  "Failed to update property": { ar: "تعذّر تحديث العقار", en: "Couldn't update the property" },
  "يجب دفع 40 ريال لإضافة عقار جديد": { ar: "استهلكت عقاراتك المجانية، إضافة عقار جديد تكلّف 40 ريال", en: "Free listings used — adding a property costs 40 SAR" },
  "يرجى رفع صور للعقار قبل الحفظ.": { ar: "ارفع صورة واحدة على الأقل للعقار", en: "Upload at least one property photo" },
  "تعذّر تحليل السعر": { ar: "تعذّر تحليل السعر", en: "Couldn't analyze the price" },
  "تعذّر توليد الوصف": { ar: "تعذّر توليد الوصف", en: "Couldn't generate the description" },

  /* projects & units */
  "Project registered successfully!": { ar: "تم تسجيل المشروع بنجاح", en: "Project registered" },
  "Project created successfully": { ar: "تم تسجيل المشروع بنجاح", en: "Project registered" },
  "Project updated successfully": { ar: "تم تحديث المشروع", en: "Project updated" },
  "Project not found": { ar: "المشروع غير موجود", en: "Project not found" },
  "Project not paid": { ar: "لم يتم دفع رسوم المشروع", en: "Project fee not paid" },
  "Failed to create project": { ar: "تعذّر تسجيل المشروع", en: "Couldn't register the project" },
  "Unit added successfully": { ar: "تمت إضافة الوحدة", en: "Unit added" },
  "Unit added successfully!": { ar: "تمت إضافة الوحدة", en: "Unit added" },
  "Unit modified successfully": { ar: "تم تحديث الوحدة", en: "Unit updated" },
  "Unit updated successfully!": { ar: "تم تحديث الوحدة", en: "Unit updated" },
  "Unit deleted successfully": { ar: "تم حذف الوحدة", en: "Unit deleted" },
  "Unit deleted successfully!": { ar: "تم حذف الوحدة", en: "Unit deleted" },
  "Unit not found": { ar: "الوحدة غير موجودة", en: "Unit not found" },
  "اختر المشروع الخاص بالوحدة": { ar: "اختر المشروع الخاص بالوحدة", en: "Select the unit's project" },

  /* ads / auctions / photography */
  "Ad created successfully!": { ar: "تم إنشاء الإعلان بنجاح", en: "Ad created" },
  "Ads request added successfully": { ar: "تم إنشاء طلب الإعلان بنجاح", en: "Ad request created" },
  "Invalid ad type or duration": { ar: "نوع الإعلان أو مدته غير صالحة", en: "Invalid ad type or duration" },
  "Auction created successfully": { ar: "تم إنشاء المزاد بنجاح", en: "Auction created" },
  "Failed to create auction": { ar: "تعذّر إنشاء المزاد", en: "Couldn't create the auction" },
  "يرجى اختيار باقة المزاد أولاً": { ar: "اختر باقة المزاد أولاً", en: "Select an auction package first" },
  "تم إنشاء الطلب بنجاح": { ar: "تم إنشاء الطلب بنجاح", en: "Request created" },
  "Request submitted successfully": { ar: "تم إرسال الطلب بنجاح", en: "Request submitted" },

  /* bank */
  "Bank details updated successfully": { ar: "تم تحديث بيانات البنك", en: "Bank details updated" },
  "Bank updated successfully": { ar: "تم تحديث بيانات البنك", en: "Bank details updated" },
  "Bank not found": { ar: "البنك غير موجود", en: "Bank not found" },
  "البنك غير موجود": { ar: "البنك غير موجود", en: "Bank not found" },
  "لا توجد معلومات حول البنك الخاص بك": { ar: "لا توجد بيانات لبنكك بعد", en: "No bank details found yet" },
  "Mortgage program created successfully": { ar: "تم إنشاء برنامج التمويل", en: "Financing program created" },
  "تم إنشاء البرنامج بنجاح وتم تفعيله": { ar: "تم إنشاء برنامج التمويل وتفعيله", en: "Financing program created and activated" },
  "تم تحديث البرنامج بنجاح وتم تفعيله": { ar: "تم تحديث برنامج التمويل", en: "Financing program updated" },
  "تم حذف البرنامج بنجاح": { ar: "تم حذف برنامج التمويل", en: "Financing program deleted" },
  "فشل إنشاء البرنامج.": { ar: "تعذّر إنشاء برنامج التمويل", en: "Couldn't create the program" },
  "البرنامج غير موجود": { ar: "البرنامج غير موجود", en: "Program not found" },
  "لا يوجد اشتراك نشط لإنشاء برنامج تمويل": { ar: "تحتاج اشتراكاً نشطاً لإنشاء برنامج تمويل", en: "An active subscription is required to create a program" },
  "نسبة التمويل والدفعة المقدمة غير صحيحة": { ar: "نسبة التمويل والدفعة المقدمة غير صحيحة", en: "Invalid financing ratio / down payment" },
  "Application status updated successfully": { ar: "تم تحديث حالة الطلب", en: "Application status updated" },
  "تم تحديث حالة الطلب بنجاح": { ar: "تم تحديث حالة الطلب", en: "Application status updated" },
  "Reason is required when rejecting an application": { ar: "يرجى كتابة سبب الرفض", en: "A reason is required to reject" },
  "سبب الرفض مطلوب": { ar: "يرجى كتابة سبب الرفض", en: "A reason is required to reject" },
  "طلب التمويل غير موجود": { ar: "طلب التمويل غير موجود", en: "Application not found" },
  "Application not found": { ar: "طلب التمويل غير موجود", en: "Application not found" },
  "يجب اضافة موعد واحد على الأقل لكل يوم": { ar: "أضف موعداً واحداً على الأقل لكل يوم", en: "Add at least one time slot per day" },
  "يجب اضافة مدينة تغطية واحدة على الأقل": { ar: "أضف مدينة تغطية واحدة على الأقل", en: "Add at least one coverage city" },

  /* documents */
  "تم رفع المستند": { ar: "تم رفع المستند", en: "Document uploaded" },
  "تم رفع المستند وسيتم مراجعته من الإدارة": { ar: "تم رفع المستند وسيُراجع من الإدارة", en: "Document uploaded — pending admin review" },
  "تم إرسال التعديل للمراجعة": { ar: "تم إرسال التعديل للمراجعة", en: "Update sent for review" },
  "تم حذف المستند": { ar: "تم حذف المستند", en: "Document deleted" },
  "تم الحذف": { ar: "تم الحذف", en: "Deleted" },
  "تعذّر رفع المستند": { ar: "تعذّر رفع المستند", en: "Couldn't upload the document" },
  "تعذّر التعديل": { ar: "تعذّر التعديل", en: "Couldn't update" },
  "تعذّر الحذف": { ar: "تعذّر الحذف", en: "Couldn't delete" },
  "المستند غير موجود": { ar: "المستند غير موجود", en: "Document not found" },
  "نوع المستند غير صالح": { ar: "نوع المستند غير صالح", en: "Invalid document type" },
  "يرجى رفع ملف المستند (PDF أو صورة)": { ar: "ارفع ملف المستند (PDF أو صورة)", en: "Upload the document file (PDF or image)" },
  "لا يمكن حذف مستند معتمد — استخدم التعديل": { ar: "لا يمكن حذف مستند معتمد، استخدم التعديل", en: "Approved documents can't be deleted — update instead" },
  "هذا المستند موجود بالفعل — استخدم «تعديل / إعادة رفع»": { ar: "هذا المستند موجود بالفعل، استخدم «تعديل / إعادة رفع»", en: "This document already exists — use “update / re-upload”" },

  /* generic */
  "Done": { ar: "تم", en: "Done" },
  "Action completed successfully": { ar: "تم بنجاح", en: "Done" },
  "An error occurred": { ar: "حدث خطأ، حاول مرة أخرى", en: "Something went wrong, please try again" },
  "Error": { ar: "حدث خطأ، حاول مرة أخرى", en: "Something went wrong, please try again" },
  "Server Error": { ar: "خطأ في الخادم، حاول لاحقاً", en: "Server error, please try again later" },
  "Server error": { ar: "خطأ في الخادم، حاول لاحقاً", en: "Server error, please try again later" },
  "Internal server error": { ar: "خطأ في الخادم، حاول لاحقاً", en: "Server error, please try again later" },
  "حدث خطأ، يرجى المحاولة مرة أخرى": { ar: "حدث خطأ، حاول مرة أخرى", en: "Something went wrong, please try again" },
  "All fields are required": { ar: "جميع الحقول مطلوبة", en: "All fields are required" },
  "جميع الحقول مطلوبة": { ar: "جميع الحقول مطلوبة", en: "All fields are required" },
  "Network Error": { ar: "تحقق من اتصالك بالإنترنت", en: "Check your internet connection" },
};

/* punctuation-insensitive lookup */
const norm = (s: string) => s.trim().replace(/[.!؟?…]+$/g, "").toLowerCase();
const INDEX = new Map<string, Pair>(Object.entries(DICT).map(([k, v]) => [norm(k), v]));

const getLocale = (): "ar" | "en" => {
  if (typeof document === "undefined") return "ar";
  const lang = document.documentElement.lang;
  if (lang === "ar" || lang === "en") return lang;
  return window.location.pathname.startsWith("/en") ? "en" : "ar";
};

export function translateMessage(raw: unknown, locale = getLocale()): string {
  const s = typeof raw === "string" ? raw : String(raw ?? "");
  const hit = INDEX.get(norm(s));
  if (hit) return hit[locale];
  if (/validation failed|cast to objectid|E11000/i.test(s)) {
    return locale === "ar" ? "البيانات المدخلة غير صالحة" : "The submitted data is invalid";
  }
  return s;
}

/* ── card ─────────────────────────────────────────────────── */
const THEME: Record<Kind, { icon: typeof CheckCircle2; accent: string; glow: string; label: Pair }> = {
  success: { icon: CheckCircle2, accent: "#2fb67c", glow: "rgba(47,182,124,0.35)", label: { ar: "تم بنجاح", en: "Success" } },
  error: { icon: XCircle, accent: "#e5484d", glow: "rgba(229,72,77,0.35)", label: { ar: "تنبيه", en: "Error" } },
  warning: { icon: AlertTriangle, accent: "#d9b25a", glow: "rgba(217,178,90,0.4)", label: { ar: "انتبه", en: "Heads up" } },
  info: { icon: Info, accent: "#5b8def", glow: "rgba(91,141,239,0.35)", label: { ar: "معلومة", en: "Info" } },
};

function Card({ id, kind, message, title, description }: { id: string | number; kind: Kind; message: string; title?: string; description?: string }) {
  const locale = getLocale();
  const ar = locale === "ar";
  const t = THEME[kind];
  const Icon = t.icon;
  return (
    <div
      dir={ar ? "rtl" : "ltr"}
      role="status"
      className="sna-toast pointer-events-auto relative flex w-[min(92vw,400px)] items-center gap-3.5 overflow-hidden rounded-2xl px-4 py-3.5 text-white"
      style={{
        background: "linear-gradient(135deg,#0f2f7a 0%,#0a1e4f 55%,#071535 100%)",
        boxShadow: `0 24px 60px -20px rgba(7,21,53,0.75), 0 0 0 1px rgba(255,255,255,0.06) inset, 0 0 40px -14px ${t.glow}`,
      }}
    >
      <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#d9b25a]/80 to-transparent" />
      <span className={`absolute inset-y-2 ${ar ? "right-0" : "left-0"} w-1 rounded-full`} style={{ background: t.accent }} />
      <span className="pointer-events-none absolute -top-10 -end-6 size-28 rounded-full blur-2xl" style={{ background: t.glow }} />

      <span
        className="relative grid size-10 shrink-0 place-items-center rounded-xl"
        style={{ background: `${t.accent}22`, color: t.accent, boxShadow: `0 0 0 1px ${t.accent}55 inset` }}
      >
        <Icon size={20} strokeWidth={2.4} />
      </span>

      <div className="relative min-w-0 flex-1">
        <p className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: t.accent }}>
          {title || t.label[locale]}
        </p>
        <p className="mt-0.5 text-sm font-bold leading-snug text-white/95">{message}</p>
        {description && <p className="mt-1 text-xs leading-relaxed text-white/60">{description}</p>}
      </div>

      <button
        onClick={() => sonner.dismiss(id)}
        aria-label="close"
        className="relative grid size-7 shrink-0 place-items-center rounded-full text-white/50 transition hover:bg-white/10 hover:text-white"
      >
        <X size={14} />
      </button>

      <span className="sna-toast-bar pointer-events-none absolute bottom-0 h-[2px]" style={{ background: t.accent, insetInlineStart: 0 }} />
    </div>
  );
}

type Opts = { title?: string; description?: string; duration?: number; id?: string | number };
const DUR: Record<Kind, number> = { success: 3500, info: 4000, warning: 4500, error: 5000 };

function fire(kind: Kind, raw: unknown, opts: Opts = {}) {
  const message = translateMessage(raw);
  if (!message) return;
  const duration = opts.duration ?? DUR[kind];
  const id = opts.id ?? `${kind}:${message}`;
  return sonner.custom(
    (tid) => <Card id={tid} kind={kind} message={message} title={opts.title} description={opts.description ? translateMessage(opts.description) : undefined} />,
    { id, duration, unstyled: true, style: { ["--sna-dur" as string]: `${duration}ms` } },
  );
}

/* drop-in replacement for sonner's `toast` */
export const toast = Object.assign((raw: unknown, opts?: Opts) => fire("info", raw, opts), {
  success: (raw: unknown, opts?: Opts) => fire("success", raw, opts),
  error: (raw: unknown, opts?: Opts) => fire("error", raw, opts),
  warning: (raw: unknown, opts?: Opts) => fire("warning", raw, opts),
  info: (raw: unknown, opts?: Opts) => fire("info", raw, opts),
  dismiss: (id?: string | number) => sonner.dismiss(id),
  fromError: (err: any, fallback = "Error", opts?: Opts) =>
    fire("error", err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback, opts),
});

export default toast;
